import { getIsGpViaMeta, getVersionEnv } from '../biz-meta'
import { getAppEnv, getEditorEnv, getIsGpViaUrlParams } from '../biz-url-params'
import { log } from '../logs'
import { debugConfig } from '../utils/get-current-debug-config'

/**
 * 判断当前环境是否是生产环境
 * 生产环境的特征：
 * 1. 不是localhost
 * 3. 不是IP地址
 * 4. 不是以'-'开头的路径
 *
 * 例如:
 * 生产上线地址为: https://pmtest2.seasungames.cn/index-allen/index.html
 * 开发/预览/预发地址为: https://pmtest2.seasungames.cn/-code/site_buider/silxoh1z141z3n7zactach6o/prod/831ba65c5ddf006837f1255d3fe7daf6/index.html
 */
export const isMasterEnv = (function () {
  const appEnv = getAppEnv() || getVersionEnv()

  if (appEnv !== null) {
    return appEnv === 'prod'
  }

  if (location.hostname === 'localhost') {
    return false
  }

  if (location.port && !['80', '443'].includes(location.port)) {
    return false
  }

  const firstHostnamePart = location.hostname.split('.')[0]

  if (['pp-zt', 'qa-zt', 'test-zt', 'dev-zt'].includes(firstHostnamePart)) {
    return false
  }

  return !/^\d+\.\d+\.\d+\.\d+$/.test(location.hostname) && !location.pathname.split('/')?.[1]?.startsWith('-')
})()

/**
 * API接口
 * @remarks 这个是作用 xxx, 在什么场景使用
 */
export interface IAPI {
  /**
   * 获取页面数据
   * @remarks 这个是作用 xxx, 在什么场景使用
   */
  getPageData: string
  /**
   * 获取图层数据
   */
  getLayerData: string
}

/**
 * 是否是GP
 */
export const isGp: boolean = debugConfig?.gp === 1 || getIsGpViaUrlParams() || getIsGpViaMeta()

/**
 * 当前版本环境
 */
export const versionEnv: 'qa' | 'pp' | 'prod' = (getAppEnv() || getVersionEnv() || (isMasterEnv ? 'prod' : 'qa')) as
  | 'qa'
  | 'pp'
  | 'prod'

/**
 * 当前系统环境
 */
export const editorEnv: 'qa' | 'pp' | 'dev' | 'prod' = debugConfig?.systemEnv || getEditorEnv() || 'prod'

/**
 * 获取主机名前缀
 */
const getHostPrefix = () => {
  switch (editorEnv) {
    case 'qa':
      return 'qa-'
    case 'pp':
      return 'pp-'
    case 'dev':
      return 'dev-'
    case 'prod':
      return ''
    default:
      return ''
  }
}

/**
 * 获取主机名设置
 */
export const getHostnameSettings = () => {
  // editorEnv 表示我发布的是大环境是那个, 比如 qa, pp, prod, 此时决定的是请求的域名
  const prefix = getHostPrefix()
  const hostnameSettings = {
    gp: {
      client: `${prefix}omni-api.seasungames.com`,
      manager: `${prefix}omni-admin.seasungames.com`,
    },
    cn: {
      client: `${prefix}omni-api.seasungames.cn`,
      manager: `${prefix}omni-admin.seasungames.cn`,
    },
  }
  return hostnameSettings[isGp ? 'gp' : 'cn']
}

/**
 * 获取主机名类型
 */
const getHostType = () => {
  if (debugConfig) {
    return 'manager'
  } else {
    return isMasterEnv ? 'client' : 'manager'
  }
}

/**
 * 获取主机名
 */
const getHostname = () => {
  // 缓存变量，用于存储生成的主机名
  let cache = null

  return (function () {
    if (cache) {
      return cache
    }
    cache = getHostnameSettings()[getHostType()]
    log.debug('cache:hostname', cache)
    return cache
  })()
}

/**
 * 获取 OMNI API 主机名
 *
 * 根据当前系统环境（editorEnv）和地区（isGp）返回对应的 OMNI API 主机名。
 *
 * 环境与地区组合对应的主机名：
 * - editorEnv = 'prod', isGp = false（国内生产环境）→ 'omni-api.seasungames.cn'
 * - editorEnv = 'prod', isGp = true（海外生产环境）→ 'omni-api.seasungames.com'
 * - editorEnv = 'pp', isGp = false（国内预发环境）→ 'pp-omni-api.seasungames.cn'
 * - editorEnv = 'pp', isGp = true（海外预发环境）→ 'pp-omni-api.seasungames.com'
 * - editorEnv = 'dev' 或 'qa', isGp = false（国内开发/测试环境）→ 'test-omni-api.seasungames.cn'
 * - editorEnv = 'dev' 或 'qa', isGp = true（海外开发/测试环境）→ 'test-omni-api.seasungames.cn'
 *
 * @returns OMNI API 主机名
 */
export const getOmniHostname = () => {
  let hostname = ''

  // 根据 editorEnv 和 isGp 返回正确的主机名
  if (editorEnv === 'prod') {
    hostname = isGp ? 'omni-api.seasungames.com' : 'omni-api.seasungames.cn'
  } else if (editorEnv === 'pp') {
    hostname = isGp ? 'pp-omni-api.seasungames.com' : 'pp-omni-api.seasungames.cn'
  } else if (editorEnv === 'dev' || editorEnv === 'qa') {
    hostname = isGp ? 'qa-omni-api.seasungames.com' : 'qa-omni-api.seasungames.cn'
  } else {
    // 未知环境，使用默认值
    log.warn(`Unknown editorEnv: ${editorEnv}, using default prod environment`)
    hostname = isGp ? 'omni-api.seasungames.com' : 'omni-api.seasungames.cn'
  }

  log.debug('getOmniHostname', { editorEnv, isGp, hostname })
  return hostname
}

/**
 * 获取当前环境的API操作
 * @returns {IAPI} 返回当前环境的API配置
 */
export const getCurrentApiActions = (function () {
  let cache: IAPI | null = null

  return (): IAPI => {
    if (cache) {
      return cache
    }

    const API_ENV: Record<string, IAPI> = {
      prod: {
        getPageData: `https://${getHostname()}/api/omni/v1/website/page_data/data_by_page_id`,
        getLayerData: `https://${getHostname()}/api/omni/v1/website/page_data/data_by_layer_id`,
      },
      pp: {
        getPageData: `https://${getHostname()}/api/cms_manager/v1/website/page_data/data_by_page_id`,
        getLayerData: `https://${getHostname()}/api/cms_manager/v1/website/page_data/data_by_layer_id`,
      },
      qa: {
        getPageData: `https://${getHostname()}/api/cms_manager/v1/website/page_data/data_by_page_id`,
        getLayerData: `https://${getHostname()}/api/cms_manager/v1/website/page_data/data_by_layer_id`,
      },
    }

    cache = API_ENV[versionEnv] || API_ENV.prod
    return cache
  }
})()

/**
 * 插件数据存储命空间
 */
export const PLUGIN_NAME_SPACE = '_plugin'
